import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useNotifications } from 'app/application/application'
import { useAudiences, useTreatments } from 'app/audiences/application'
import { AudienceConfiguration, AudienceType, Treatment } from 'app/audiences/types'
import { AudienceConfigurationConnection } from 'app/audiences/types/audiences'
import { Form, PageHeader } from 'app/common/presentation'
import ConnectionIcon from 'app/common/presentation/Connection/ConnectionIcon'
import { useConnections } from 'app/connections/application'
import { ConnectionName } from 'app/connections/types'

import { Button } from 'components'

import styles from './CreateAudience.module.scss'

const animals = [
  'lion',
  'elephant',
  'dolphin',
  'penguin',
  'giraffe',
  'kangaroo',
  'koala',
  'tiger',
  'polarBear',
  'gorilla',
  'cheetah',
  'panda',
  'octopus',
  'eagle',
  'chimpanzee',
  'crocodile',
  'zebra',
  'wolf',
  'seahorse',
  'hummingbird',
]

const generateKey = () => {
  const key = Math.floor(Math.random() * 1000000000)
  const randomAnimal = animals[Math.floor(Math.random() * animals.length)]
  return randomAnimal + key
}

const validConnectionNamesForCreation: ConnectionName[] = ['facebook', 'googleAnalytics', 'tiktok']
const validConnectionNames: ConnectionName[] = [...validConnectionNamesForCreation, 'googleAdwords']
const validTypes: AudienceType[] = ['exclusion', 'retargeting', 'seed']

const CreateAudience = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('audiences')
  const { t: tConnections } = useTranslation('connections')

  const { createNotification, te } = useNotifications()
  const { createAudience, isValidConfiguration } = useAudiences()
  const { connections } = useConnections()
  const { getTreatments } = useTreatments()

  const [audienceConfiguration, setAudienceConfiguration] = useState<AudienceConfiguration>({
    audienceKey: generateKey(),
    treatments: [],
  })
  const [treatmentsForConnection, setTreatmentsForConnection] = useState<Treatment[]>([])
  const [adAccountsError, setAdAccountsError] = useState<string>()

  const validConnections = useMemo(
    () =>
      connections.filter(
        (conn) =>
          validConnectionNames.includes(conn.name) &&
          conn.status === 'active' &&
          conn.targetingAvailable,
      ),
    [connections],
  )
  const displayedConnections: AudienceConfigurationConnection[] = useMemo(
    () =>
      connections.filter((conn): conn is AudienceConfigurationConnection =>
        validConnectionNamesForCreation.includes(conn.name),
      ),
    [connections],
  )

  useEffect(() => {
    if (validConnections.length) {
      void getTreatments({
        connection: validConnections.map((validConnection) => validConnection.id),
      }).then((result) =>
        result.ok
          ? setTreatmentsForConnection(result.val)
          : createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            }),
      )
    }
  }, [validConnections, getTreatments, createNotification, te])

  return (
    <div className={styles.createAudience}>
      <PageHeader docu={t('create.docuLink')} title={t('create.headline')} hasBack />
      <div className={styles.form}>
        <Form.InputFormRow
          description={t('create.name.description')}
          mandatory
          onChange={(name) => setAudienceConfiguration((prev) => ({ ...prev, name }))}
          title={t('create.name.title')}
          value={audienceConfiguration.name ?? ''}
        />
        <Form.TextFieldFormRow
          description={t('create.description.description')}
          onChange={(description) => setAudienceConfiguration((prev) => ({ ...prev, description }))}
          placeholder={t('create.description.placeholder')}
          title={t('create.description.title')}
          value={audienceConfiguration.description ?? ''}
        />
        <Form.DropDownFormRow
          description={t('create.type.description')}
          data={validTypes}
          mandatory
          onSelect={(type) => setAudienceConfiguration((prev) => ({ ...prev, type }))}
          renderValueContent={(value) => t(`type.${value}`)}
          selected={audienceConfiguration.type ?? null}
          title={t('create.type.title')}
        />
        <Form.DropDownFormRow
          description={t('create.connection.description')}
          data={displayedConnections}
          mandatory
          onSelect={(connection) =>
            setAudienceConfiguration((prev) => ({ ...prev, connection: connection }))
          }
          renderValueContent={(value) => (
            <div className={styles.dropDownValue}>
              <ConnectionIcon connection={value} />
              {tConnections(`available.${value.name}.displayName`)}
            </div>
          )}
          selected={audienceConfiguration.connection ?? null}
          title={t('create.connection.title')}
        />
        {audienceConfiguration.connection?.name === 'facebook' && (
          <>
            <Form.DropDownFormRow
              description={t('create.adAccount.description', {
                connection: 'Meta',
              })}
              data={audienceConfiguration.connection.config.accounts}
              error={adAccountsError}
              mandatory
              onSelect={(adAccount) =>
                setAudienceConfiguration((prev) => ({ ...prev, adAccountId: adAccount.id }))
              }
              renderValueContent={(account) => `${account.name} (${account.id})`}
              selected={
                audienceConfiguration.connection.config.accounts.find(
                  (account) => account.id === audienceConfiguration.adAccountId,
                ) ?? null
              }
              title={t('create.adAccount.title')}
            />
            <Form.DropDownFormRow
              description={t('create.pixelId.description')}
              data={[audienceConfiguration.connection.config.pixelId]}
              mandatory
              onSelect={(pixelId) => setAudienceConfiguration((prev) => ({ ...prev, pixelId }))}
              selected={audienceConfiguration.pixelId ?? null}
              title={t('create.pixelId.title')}
            />
          </>
        )}
        {audienceConfiguration.connection?.name === 'tiktok' && (
          <Form.DropDownFormRow
            description={t('create.adAccount.description', {
              connection: 'TikTok',
            })}
            data={audienceConfiguration.connection.config.adAccountIds.split(';')}
            mandatory
            onSelect={(adAccountId) =>
              setAudienceConfiguration((prev) => ({ ...prev, adAccountId }))
            }
            selected={audienceConfiguration.adAccountId ?? null}
            title={t('create.adAccount.title')}
          />
        )}
        {audienceConfiguration.connection?.name === 'googleAnalytics' && (
          <Form.InputFormRow
            description={t('create.audienceKey.description')}
            mandatory
            title={t('create.audienceKey.title')}
            value={audienceConfiguration.audienceKey}
          />
        )}
        <Form.DropDownFormRow
          description={t('create.treatments.description')}
          data={treatmentsForConnection}
          onSelect={(value) =>
            setAudienceConfiguration((prev) => ({
              ...prev,
              treatments: prev.treatments.some((treatment) => treatment.id === value.id)
                ? prev.treatments.filter((treatment) => treatment.id !== value.id)
                : [...prev.treatments, value],
            }))
          }
          renderValueContent={(value) => value.campaign.name}
          selection={audienceConfiguration.treatments}
          title={t('create.treatments.title')}
          type="multiple"
        />
      </div>
      <Button
        className={styles.add}
        disabled={!isValidConfiguration(audienceConfiguration)}
        onClick={() => {
          setAdAccountsError(undefined)
          void createAudience(audienceConfiguration).then((result) => {
            if (result.ok) {
              if (result.val) {
                createNotification({
                  message: t('create.success.message', { name: audienceConfiguration.name }),
                  title: t('create.success.title'),
                  type: 'success',
                })

                void navigate('/audiences')
              }
            } else {
              if (result.val.code === 502) {
                setAdAccountsError(result.val.message)
              }
            }
          })
        }}
      >
        {t('create.add')}
      </Button>
    </div>
  )
}

export default CreateAudience
